/**
 * Derives the player's sources/subtitles once a playable source first appears,
 * then freezes — so a later content change does not rebuild a playing player.
 * Retries a not-yet-resolvable stream URL with capped backoff.
 *
 * Audio-only guard: if video transcodings are still in progress and only
 * audio-only has completed, delays committing sources and freezing. The effect
 * re-runs when transcoding states change (via transcodingStateSignature dep).
 * This ensures the player only mounts once video sources are available.
 */

import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import Api from '@plone/volto/helpers/Api/Api';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { getVideoPlayerData } from 'volto-base-addon/components/manage/Blocks/VideoJS/VideoPlayerDataHook';
import {
  derivePlayerState,
  shouldSuppressAudioOnlyMount,
  outputStateOf,
} from 'volto-base-addon/helpers/mediaProcessingLogic';

export const useMediaPlayerSources = (content) => {
  const { token } = useSelector((state) => state.userSession);
  const intl = useIntl();

  const [playerData, setPlayerData] = useState({ isReady: false, data: {} });
  const [playerDataFailed, setPlayerDataFailed] = useState(false);

  const objectId = content?.s3_object_id;
  const liveStreamUrl = content?.live_stream_url;
  const uid = content?.UID;
  const contentPath = content?.['@id'] ? flattenToAppURL(content['@id']) : null;

  // Re-derive only while no working source has loaded; the source id and the
  // resolvable stream may appear at any time, separately.
  const sourceSignature = `${objectId || ''}|${liveStreamUrl || ''}`;
  const loadedRef = useRef(false);
  const retriesRef = useRef(0);
  const [retryTick, setRetryTick] = useState(0);

  // Transcoding state signature: when a transcoding child transitions to a
  // new media_output_workflow state (completed/failed) and the enriched
  // content is refetched, this changes, which re-triggers the source
  // derivation effect. This is the mechanism that allows the player to upgrade
  // from "nothing" to "video" once the SMIL is updated with video profiles.
  const items = Array.isArray(content?.items) ? content.items : [];
  const transcodingStateSignature = items
    .filter((i) => i['@type'] === 'transcoding')
    .map((i) => `${i.title}:${outputStateOf(i) || ''}`)
    .join(',');

  // Surface the fallback after a few tries but keep retrying (self-heals);
  // cap the interval.
  const FAIL_AFTER = 3;
  const RETRY_BASE_MS = 4000;
  const RETRY_MAX_MS = 30000;

  useEffect(() => {
    if (loadedRef.current) {
      return; // a working source is mounted; never rebuild a playing player
    }

    if (!uid || (!objectId && !liveStreamUrl)) {
      setPlayerData({ isReady: true, data: {} }); // nothing playable yet
      return;
    }

    const controller = new AbortController();
    const api = new Api();
    let isMounted = true;
    let retryTimer;

    const scheduleRetry = () => {
      retriesRef.current += 1;
      if (retriesRef.current >= FAIL_AFTER) {
        setPlayerDataFailed(true);
      }
      const delay = Math.min(RETRY_BASE_MS * retriesRef.current, RETRY_MAX_MS);
      retryTimer = setTimeout(() => {
        if (isMounted) {
          setRetryTick((t) => t + 1);
        }
      }, delay);
    };

    getVideoPlayerData(
      items,
      objectId,
      liveStreamUrl,
      uid,
      token,
      api,
      controller.signal,
      contentPath,
      intl,
    )
      .then((result) => {
        if (!isMounted) {
          return;
        }
        const usable = !!(
          result &&
          (result.sources?.length || result.subtitles?.length)
        );
        if (usable) {
          // Check whether to commit or wait. If video transcodings are still
          // in progress and only audio-only has completed, don't commit data
          // and don't freeze. This keeps playerState at WAITING, preventing
          // the player from mounting with audio-only content. The effect will
          // re-run when transcodingStateSignature changes.
          const suppress = shouldSuppressAudioOnlyMount(items);

          if (suppress) {
            // Sources exist but are audio-only while video is still coming.
            // Signal "ready but no data" so the view shows progress, not the
            // player. Don't freeze — we'll re-derive on next state change.
            setPlayerData({ isReady: true, data: {} });
          } else {
            loadedRef.current = true;
            setPlayerData({ isReady: true, data: result });
          }
          retriesRef.current = 0;
          setPlayerDataFailed(false);
        } else {
          // Source id set but no resolvable stream yet — try again soon.
          setPlayerData({ isReady: true, data: {} });
          scheduleRetry();
        }
      })
      .catch((err) => {
        if (err.name === 'AbortError' || !isMounted) {
          return;
        }
        // eslint-disable-next-line no-console
        console.error('Error fetching video player data:', err);
        setPlayerData({ isReady: true, data: {} });
        scheduleRetry();
      });

    return () => {
      isMounted = false;
      controller.abort();
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceSignature, uid, retryTick, transcodingStateSignature]);

  const playerState = derivePlayerState({
    isReady: playerData.isReady,
    dataHasKeys: Object.keys(playerData.data).length > 0,
    objectId,
    playerDataFailed,
  });

  return { playerData, playerState, playerDataFailed };
};

export default useMediaPlayerSources;
